import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { formatDate } from "../utils/formatDate";
import { auth, db } from "../firebase";

const FUNCTION_URL = "https://us-central1-solis-travel.cloudfunctions.net/parseDocument";

const TYPE_CONFIG = {
  flight: { icon: "✈️", label: "Flight", color: "#e3eef6", textColor: "#1E2D4A" },
  hotel: { icon: "🏨", label: "Hotel", color: "#E1F5EE", textColor: "#085041" },
  car_rental: { icon: "🚗", label: "Car Rental", color: "#FAEEDA", textColor: "#633806" },
  activity: { icon: "🎭", label: "Activity", color: "#EDE0F5", textColor: "#4A1080" },
};

export default function Bookings({ trip, onBack, onUpdateItinerary }) {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [smartBanner, setSmartBanner] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const tripSnap = await getDoc(doc(db, "trips", trip.id));
      if (tripSnap.exists()) {
        const rawBookings = tripSnap.data().bookings || [];
        const needsBackfill = rawBookings.some(b => !b.id);
        const bookings = rawBookings.map(b => b.id ? b : { ...b, id: crypto.randomUUID() });
        if (needsBackfill) {
          await updateDoc(doc(db, "trips", trip.id), { bookings });
        }
        setBookings(bookings);
      }
    } catch (err) {
      console.error(err);
      setFetchError("Failed to load bookings. Please try again.");
    }
  };

  const hasTimeSensitiveFields = (b) => {
    if (b.type === "flight") return !!(b.departureTime || b.arrivalTime);
    if (b.type === "hotel") return !!(b.checkIn || b.checkOut);
    return !!(b.time || b.date);
  };

  const buildBannerMessage = (b) => {
    if (b.type === "flight") {
      if (b.arrivalTime && b.arrivalDate)
        return `Your flight lands at ${b.arrivalTime} on ${formatDate(b.arrivalDate)} — want us to update your itinerary to account for travel time?`;
      if (b.departureTime && b.departureDate)
        return `Your flight departs at ${b.departureTime} on ${formatDate(b.departureDate)} — want us to update your itinerary?`;
      return "Your flight affects your trip schedule — want us to update your itinerary?";
    }
    if (b.type === "hotel") {
      const name = b.hotel || b.name || "hotel";
      return `Your ${name} check-in is on ${b.checkIn ? formatDate(b.checkIn) : "arrival day"} — want us to update your itinerary with nearby activities?`;
    }
    if (b.type === "activity" && b.time) {
      const name = b.name || "activity";
      return `Your ${name} is booked at ${b.time}${b.date ? ` on ${formatDate(b.date)}` : ""} — want us to fit this into your itinerary?`;
    }
    return "This booking affects your trip dates — want us to update your itinerary?";
  };

  const handleParse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setError("");
    setParsed(null);
    try {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, tripId: trip.id }),
      });
      const data = await response.json();
      const content = data.content?.[0]?.text;
      if (!content) throw new Error("No response from AI");
      const clean = content.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean.slice(clean.indexOf("{"), clean.lastIndexOf("}") + 1));
      setParsed(parsed);
    } catch (err) {
      setError("Couldn't parse this document. Try pasting more of the email.");
    }
    setParsing(false);
  };

  const handleSave = async () => {
    if (!auth.currentUser) {
      console.error("Bookings.handleSave: auth.currentUser is null - session likely expired");
      setError("You've been signed out. Please sign in again.");
      return;
    }
    setSaving(true);
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const addedByName = userSnap.exists() ? userSnap.data().name?.split(" ")[0] || "You" : "You";

      const booking = {
        ...parsed,
        id: crypto.randomUUID(),
        addedBy: auth.currentUser.uid,
        addedByName,
        addedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, "trips", trip.id), {
        bookings: arrayUnion(booking),
      });
      setBookings(prev => [...prev, booking]);
      setParsed(null);
      setText("");
      setShowImport(false);
      if (hasTimeSensitiveFields(booking)) {
        setSmartBanner(booking);
      }
    } catch (err) {
      console.error("Bookings.handleSave failed:", err);
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  };

  const handleDelete = async (booking) => {
    setBookings(prev => prev.filter(b => b.id !== booking.id));
    setSelectedBooking(null);
    try {
      const tripSnap = await getDoc(doc(db, "trips", trip.id));
      const current = tripSnap.exists() ? (tripSnap.data().bookings || []) : [];
      await updateDoc(doc(db, "trips", trip.id), {
        bookings: current.filter(b => b.id !== booking.id),
      });
    } catch (err) {
      console.error(err);
      setBookings(prev => [...prev, booking]);
      setSelectedBooking(booking);
      setFetchError("Failed to delete booking. Please try again.");
    }
  };

  const groupedBookings = bookings.reduce((acc, b) => {
    const type = b.type || "other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(b);
    return acc;
  }, {});

  // ── IMPORT SCREEN ──────────────────────────────────────────────────────────
  if (showImport) return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .bk-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; padding-bottom: 40px; }
        .bk-nav { background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
        .bk-back { font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .bk-nav-title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: #1a1714; }
        .bk-content { padding: 20px; }
        .bk-card { background: white; border-radius: 16px; border: 0.5px solid rgba(26,23,20,0.08); padding: 20px; margin-bottom: 16px; }
        .bk-textarea { width: 100%; padding: 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: #fafaf8; font-size: 13px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; resize: none; height: 200px; line-height: 1.6; }
        .bk-textarea:focus { border-color: #F5A623; background: white; }
        .bk-btn { width: 100%; padding: 14px; background: #F5A623; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; margin-top: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .bk-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .bk-btn-secondary { width: 100%; padding: 13px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; margin-top: 8px; }
        .bk-error { background: #fef0ed; border: 1px solid rgba(201,98,47,0.2); border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #c9622f; margin-top: 12px; }
        .bk-result { background: white; border-radius: 16px; border: 0.5px solid rgba(26,23,20,0.08); overflow: hidden; margin-bottom: 16px; }
        .bk-result-header { padding: 16px 20px; display: flex; align-items: center; gap: 12px; border-bottom: 0.5px solid rgba(26,23,20,0.06); }
        .bk-result-icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
        .bk-result-body { padding: 16px 20px; }
        .bk-detail-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 0.5px solid rgba(26,23,20,0.05); }
        .bk-detail-row:last-child { border-bottom: none; }
        .bk-detail-label { font-size: 12px; color: rgba(26,23,20,0.45); }
        .bk-detail-value { font-size: 13px; font-weight: 500; color: #1a1714; text-align: right; max-width: 60%; }
        .bk-tip { background: #e3eef6; border-radius: 12px; padding: 12px 16px; display: flex; gap: 10px; margin-bottom: 16px; }
        .bk-tip-text { font-size: 12px; color: #1E2D4A; line-height: 1.6; }
        .bk-save-btn { flex: 1; padding: 13px; background: #1D9E75; color: white; border: none; border-radius: 12px; font-size: 14px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; }
        .bk-actions { display: flex; gap: 10px; padding: 16px 20px; border-top: 0.5px solid rgba(26,23,20,0.06); }
        .bk-retry { padding: 13px 20px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="bk-root">
        <div className="bk-nav">
          <button className="bk-back" onClick={() => { setShowImport(false); setParsed(null); setText(""); setError(""); }}>← Back</button>
          <span className="bk-nav-title">Import booking</span>
          <div style={{width:60}}></div>
        </div>
        <div className="bk-content">
          {!parsed ? (
            <>
              <div className="bk-tip">
                <span style={{fontSize:16}}>💡</span>
                <div className="bk-tip-text">Open your confirmation email, select all (Cmd+A), copy and paste below. Works for flights, hotels, car rentals and activities.</div>
              </div>
              <div className="bk-card">
                <div style={{fontSize:14,fontWeight:500,color:'#1a1714',marginBottom:12}}>Paste confirmation email</div>
                <textarea className="bk-textarea" placeholder="Paste your full confirmation email here..." value={text} onChange={e => setText(e.target.value)} />
                <div style={{fontSize:11,color:'rgba(26,23,20,0.35)',textAlign:'right',marginTop:6}}>{text.length} characters</div>
                <button className="bk-btn" onClick={handleParse} disabled={parsing || text.length < 50}>
                  {parsing ? "Reading your booking..." : "✨ Extract booking details"}
                </button>
                {error && <div className="bk-error">{error}</div>}
              </div>
            </>
          ) : (
            <>
              {(() => {
                const config = TYPE_CONFIG[parsed.type] || { icon: "📄", label: "Booking", color: "#FAF6F0", textColor: "#1a1714" };
                return (
                  <div className="bk-result">
                    <div className="bk-result-header">
                      <div className="bk-result-icon" style={{background: config.color}}>
                        {config.icon}
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:500,textTransform:'uppercase',letterSpacing:'0.06em',color:config.textColor,marginBottom:2}}>{config.label}</div>
                        <div style={{fontFamily:'Playfair Display, serif',fontSize:17,fontWeight:600,color:'#1a1714'}}>
                          {parsed.name || parsed.airline || parsed.hotel || parsed.company || "Booking"}
                        </div>
                      </div>
                    </div>
                    <div className="bk-result-body">
                      {[
                        { label: "Confirmation #", value: parsed.confirmationNumber },
                        { label: "Flight", value: parsed.flightNumber },
                        { label: "From", value: parsed.from },
                        { label: "To", value: parsed.to },
                        { label: "Departure", value: parsed.departureDate && `${formatDate(parsed.departureDate)} ${parsed.departureTime || ""}`.trim() },
                        { label: "Arrival", value: parsed.arrivalDate && `${formatDate(parsed.arrivalDate)} ${parsed.arrivalTime || ""}`.trim() },
                        { label: "Check-in", value: parsed.checkIn && formatDate(parsed.checkIn) },
                        { label: "Check-out", value: parsed.checkOut && formatDate(parsed.checkOut) },
                        { label: "Address", value: parsed.address },
                        { label: "Date", value: parsed.date && formatDate(parsed.date) },
                        { label: "Time", value: parsed.time },
                        { label: "Total", value: parsed.totalPrice },
                        { label: "Notes", value: parsed.notes },
                      ].filter(r => r.value).map((row, i) => (
                        <div key={i} className="bk-detail-row">
                          <span className="bk-detail-label">{row.label}</span>
                          <span className="bk-detail-value">{row.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bk-actions">
                      <button className="bk-retry" onClick={() => setParsed(null)}>Try again</button>
                      <button className="bk-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save to trip ✓"}
                      </button>
                    </div>
                  </div>
                );
              })()}
              {error && <div className="bk-error">{error}</div>}
            </>
          )}
        </div>
      </div>
    </>
  );

  // ── BOOKING DETAIL ─────────────────────────────────────────────────────────
  if (selectedBooking) {
    const config = TYPE_CONFIG[selectedBooking.type] || { icon: "📄", label: "Booking", color: "#FAF6F0", textColor: "#1a1714" };
    return (
      <div style={{minHeight:'100vh',background:'#FAF6F0',fontFamily:'DM Sans, sans-serif',paddingBottom:40}}>
        <div style={{background:'white',borderBottom:'0.5px solid rgba(26,23,20,0.08)',padding:'16px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100}}>
          <button onClick={() => setSelectedBooking(null)} style={{fontSize:14,fontWeight:500,color:'rgba(26,23,20,0.55)',cursor:'pointer',background:'none',border:'none',fontFamily:'DM Sans, sans-serif'}}>← Back</button>
          <span style={{fontFamily:'Playfair Display, serif',fontSize:16,fontWeight:600,color:'#1a1714'}}>{config.label} details</span>
          <button onClick={() => handleDelete(selectedBooking)} style={{fontSize:12,color:'#c9622f',background:'#fef0ed',border:'none',borderRadius:100,padding:'6px 14px',cursor:'pointer',fontFamily:'DM Sans, sans-serif'}}>Delete</button>
        </div>

        <div style={{padding:20}}>
          <div style={{background:'white',borderRadius:16,overflow:'hidden',border:'0.5px solid rgba(26,23,20,0.08)'}}>
            <div style={{padding:'20px',background:config.color,display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:52,height:52,borderRadius:16,background:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>
                {config.icon}
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:500,textTransform:'uppercase',letterSpacing:'0.06em',color:config.textColor,marginBottom:4}}>{config.label}</div>
                <div style={{fontFamily:'Playfair Display, serif',fontSize:20,fontWeight:600,color:'#1a1714'}}>
                  {selectedBooking.name || selectedBooking.airline || selectedBooking.hotel || selectedBooking.company || "Booking"}
                </div>
              </div>
            </div>
            <div style={{padding:'0 20px'}}>
              {[
                { label: "Confirmation #", value: selectedBooking.confirmationNumber },
                { label: "Flight number", value: selectedBooking.flightNumber },
                { label: "From", value: selectedBooking.from },
                { label: "To", value: selectedBooking.to },
                { label: "Departure", value: selectedBooking.departureDate && `${formatDate(selectedBooking.departureDate)} ${selectedBooking.departureTime || ""}`.trim() },
                { label: "Arrival", value: selectedBooking.arrivalDate && `${formatDate(selectedBooking.arrivalDate)} ${selectedBooking.arrivalTime || ""}`.trim() },
                { label: "Check-in", value: selectedBooking.checkIn && formatDate(selectedBooking.checkIn) },
                { label: "Check-out", value: selectedBooking.checkOut && formatDate(selectedBooking.checkOut) },
                { label: "Address", value: selectedBooking.address },
                { label: "Date", value: selectedBooking.date && formatDate(selectedBooking.date) },
                { label: "Time", value: selectedBooking.time },
                { label: "Total paid", value: selectedBooking.totalPrice },
                { label: "Notes", value: selectedBooking.notes },
              ].filter(r => r.value).map((row, i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:'0.5px solid rgba(26,23,20,0.05)'}}>
                  <span style={{fontSize:13,color:'rgba(26,23,20,0.45)'}}>{row.label}</span>
                  <span style={{fontSize:14,fontWeight:500,color:'#1a1714',textAlign:'right',maxWidth:'60%'}}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN BOOKINGS LIST ────────────────────────────────────────────────────
  return (
    <div style={{minHeight:'100vh',background:'#FAF6F0',fontFamily:'DM Sans, sans-serif',paddingBottom:40}}>
      <div style={{background:'white',borderBottom:'0.5px solid rgba(26,23,20,0.08)',padding:'16px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100}}>
        <button onClick={onBack} style={{fontSize:14,fontWeight:500,color:'rgba(26,23,20,0.55)',cursor:'pointer',background:'none',border:'none',fontFamily:'DM Sans, sans-serif'}}>← Back</button>
        <span style={{fontFamily:'Playfair Display, serif',fontSize:16,fontWeight:600,color:'#1a1714'}}>Bookings</span>
        <button onClick={() => setShowImport(true)} style={{fontSize:12,fontWeight:500,color:'white',background:'#F5A623',border:'none',borderRadius:100,padding:'7px 16px',cursor:'pointer',fontFamily:'DM Sans, sans-serif'}}>+ Import</button>
      </div>

      <div style={{background:'#1E2D4A',padding:'24px'}}>
        <div style={{fontFamily:'Playfair Display, serif',fontSize:22,fontWeight:600,color:'white',marginBottom:4}}>{trip.name}</div>
        <div style={{fontSize:13,color:'rgba(255,255,255,0.5)'}}>📍 {trip.destination} · {bookings.length} booking{bookings.length !== 1 ? 's' : ''}</div>
      </div>

      {smartBanner && (
        <div style={{margin:'16px 20px 0',background:'#FEF0DC',border:'1px solid rgba(245,166,35,0.35)',borderRadius:16,padding:'14px 16px'}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:12}}>
            <span style={{fontSize:20,flexShrink:0,marginTop:1}}>
              {smartBanner.type === 'flight' ? '✈️' : smartBanner.type === 'hotel' ? '🏨' : smartBanner.type === 'car_rental' ? '🚗' : '🎭'}
            </span>
            <div style={{fontSize:13,fontWeight:500,color:'#1E2D4A',lineHeight:1.55}}>
              {buildBannerMessage(smartBanner)}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button
              onClick={() => { if (onUpdateItinerary) onUpdateItinerary(smartBanner); setSmartBanner(null); }}
              style={{flex:1,padding:'10px',background:'#F5A623',color:'#1E2D4A',border:'none',borderRadius:10,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}
            >
              Update Itinerary ✨
            </button>
            <button
              onClick={() => setSmartBanner(null)}
              style={{padding:'10px 14px',background:'white',color:'rgba(26,23,20,0.55)',border:'1px solid rgba(26,23,20,0.12)',borderRadius:10,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}
            >
              Keep as is
            </button>
          </div>
        </div>
      )}

      <div style={{padding:20}}>
        {fetchError && (
          <p style={{fontSize:13,color:'#c9622f',marginBottom:12}}>{fetchError}</p>
        )}
        {bookings.length === 0 ? (
          <div style={{background:'white',borderRadius:16,padding:40,textAlign:'center',border:'1.5px dashed rgba(26,23,20,0.12)'}}>
            <div style={{fontSize:36,marginBottom:12}}>🗂️</div>
            <div style={{fontSize:16,fontWeight:500,color:'#1a1714',marginBottom:6}}>No bookings yet</div>
            <div style={{fontSize:13,color:'rgba(26,23,20,0.45)',marginBottom:20,lineHeight:1.5}}>Import your flight, hotel or activity confirmations and they'll all live here.</div>
            <button onClick={() => setShowImport(true)} style={{padding:'10px 20px',background:'#F5A623',color:'white',border:'none',borderRadius:100,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'DM Sans, sans-serif'}}>
              Import first booking
            </button>
          </div>
        ) : (
          Object.entries(groupedBookings).map(([type, items]) => {
            const config = TYPE_CONFIG[type] || { icon: "📄", label: "Other", color: "#FAF6F0", textColor: "#1a1714" };
            return (
              <div key={type} style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:500,color:'rgba(26,23,20,0.5)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10}}>
                  {config.icon} {config.label}{items.length > 1 ? 's' : ''}
                </div>
                {items.map((booking, i) => (
                  <div key={i}
                    onClick={() => setSelectedBooking(booking)}
                    style={{background:'white',borderRadius:14,border:'0.5px solid rgba(26,23,20,0.08)',padding:16,marginBottom:8,cursor:'pointer',transition:'transform 0.15s',display:'flex',alignItems:'center',gap:12}}
                  >
                    <div style={{width:42,height:42,borderRadius:12,background:config.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
                      {config.icon}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:14,fontWeight:500,color:'#1a1714',marginBottom:2}}>
                        {booking.name || booking.airline || booking.hotel || booking.company || "Booking"}
                      </div>
                      <div style={{fontSize:12,color:'rgba(26,23,20,0.45)',marginBottom:4}}>
                        {booking.confirmationNumber && `#${booking.confirmationNumber}`}
                        {booking.departureDate && ` · ${formatDate(booking.departureDate)}`}
                        {booking.checkIn && ` · Check-in ${formatDate(booking.checkIn)}`}
                        {booking.date && ` · ${formatDate(booking.date)}`}
                      </div>
                      {booking.addedByName && (
                        <div style={{display:'inline-flex',alignItems:'center',gap:4,background:'#FAF6F0',borderRadius:100,padding:'2px 8px'}}>
                          <div style={{width:14,height:14,borderRadius:'50%',background:'#F5A623',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:'white',fontWeight:600}}>
                            {booking.addedByName.charAt(0).toUpperCase()}
                          </div>
                          <span style={{fontSize:10,color:'rgba(26,23,20,0.55)',fontWeight:500}}>{booking.addedBy === auth.currentUser?.uid ? "You" : booking.addedByName}</span>
                        </div>
                      )}
                    </div>
                    <div style={{fontSize:16,color:'rgba(26,23,20,0.25)'}}>›</div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}